import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { useProcessApiClient } from '@/api/clients/useProcessApiClient.util.ts';
import { ProcessListItemDto } from '@/api/generated';
import { QueryKeyEnum } from '@/api/model/QueryKey.enum.ts';
import { CustomTableRequestModel } from '@/models/CustomTableRequest.model.ts';
import { formatDateToBackend } from '@/utils/formatDate.util.ts';
import { ProcessesListSearchFormModel } from '@/views/processes/list/model/ProcessesListSearchForm.model.ts';

type Req = CustomTableRequestModel<ProcessesListSearchFormModel>;

type Response = {
  items: ProcessListItemDto[];
  totalPages: number;
  totalResults: number;
};

type Return = {
  getProcesses: (req: Req) => Promise<Response>;
  isLoading: boolean;
  notExecuted: boolean;
};

export const useGetProcessesList = (): Return => {
  const { processesApi } = useProcessApiClient();

  const { isIdle, isPending, mutateAsync } = useMutation<Response, AxiosError, Req>({
    meta: { disableSuccessAlert: true },
    mutationFn: async req => {
      const { params } = req;
      const res = await processesApi.searchProcesses(req.page, req.pageSize, {
        ...req.params,
        createdFrom: formatDateToBackend(params.createdFrom),
        createdTo: formatDateToBackend(params.createdTo),
        customerId: params.customerId?.value ?? undefined,
        inquirySources: params.inquirySources?.map(it => it.value),
        isNeedingAttention: params.processOwnerId ? params.isNeedingAttention : undefined,
        processContactId: params.processContactId?.value ?? undefined,
        processCreatorId: params.processCreatorId?.value ?? undefined,
        processOwnerId: params.processOwnerId?.value ?? undefined,
        statuses: params.statuses ? params.statuses.map(it => it.value) : undefined,
        updatedFrom: formatDateToBackend(params.updatedFrom),
        updatedTo: formatDateToBackend(params.updatedTo),
      });

      return res.data;
    },
    mutationKey: [QueryKeyEnum.PROCESSES.SEARCH],
  });

  return {
    getProcesses: mutateAsync,
    isLoading: isPending,
    notExecuted: isIdle,
  };
};
